import React, { useEffect, useState, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';

const ROLE_AR = { admin: 'مدير', supervisor: 'مشرف', team: 'فريق' };

export default function Broadcast() {
  const { api, user } = useAuth();
  const toast = useToast();
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [filter, setFilter] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api('/admin/broadcast/recipients')
      .then(setUsers)
      .catch(e => toast.error(e.message));
  }, []);

  const filtered = useMemo(() => {
    const q = filter.trim();
    if (!q) return users;
    return users.filter(u => u.name.includes(q));
  }, [users, filter]);

  const toggleAll = (role) => {
    const ids = users.filter(u => !role || u.role === role).map(u => u.id);
    const next = new Set(selected);
    const allSelected = ids.every(id => next.has(id));
    ids.forEach(id => allSelected ? next.delete(id) : next.add(id));
    setSelected(next);
  };

  const toggleOne = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };

  const send = async () => {
    if (!title.trim() || !message.trim()) {
      toast.error('العنوان والرسالة مطلوبان');
      return;
    }
    if (selected.size === 0) {
      toast.error('اختر مستلماً واحداً على الأقل');
      return;
    }
    if (!window.confirm(`إرسال الرسالة إلى ${selected.size} مستخدم؟`)) return;
    setSending(true);
    try {
      const r = await api('/admin/broadcast', {
        method: 'POST',
        body: JSON.stringify({
          title: title.trim(),
          message: message.trim(),
          user_ids: Array.from(selected),
        }),
      });
      toast.success(r.message || 'تم الإرسال');
      setTitle('');
      setMessage('');
      setSelected(new Set());
    } catch (e) {
      toast.error(e.message || 'فشل الإرسال');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <h2>📣 رسالة عامة</h2>
        <p style={{ color: 'var(--text-muted)' }}>
          أرسل رسالة من الإدارة إلى مستخدمين مختارين عبر التطبيق والبريد والتلجرام والواتساب (حسب تفضيلات كل مستخدم).
        </p>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="form-group">
          <label className="form-label">العنوان</label>
          <input
            className="form-input"
            value={title}
            onChange={e => setTitle(e.target.value)}
            maxLength={120}
            placeholder="مثال: اجتماع طارئ غداً"
          />
        </div>
        <div className="form-group">
          <label className="form-label">الرسالة</label>
          <textarea
            className="form-input"
            rows={5}
            value={message}
            onChange={e => setMessage(e.target.value)}
            maxLength={2000}
            placeholder="نص الرسالة..."
          />
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
          <strong>المستلمون ({selected.size}/{users.length})</strong>
          <input
            className="form-input"
            placeholder="بحث بالاسم..."
            value={filter}
            onChange={e => setFilter(e.target.value)}
            style={{ flex: 1, minWidth: 180 }}
          />
          <button className="btn btn-secondary btn-sm" onClick={() => toggleAll(null)}>تحديد/إلغاء الكل</button>
          <button className="btn btn-secondary btn-sm" onClick={() => toggleAll('team')}>الفريق</button>
          <button className="btn btn-secondary btn-sm" onClick={() => toggleAll('supervisor')}>المشرفون</button>
          <button className="btn btn-secondary btn-sm" onClick={() => toggleAll('admin')}>المديرون</button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 8, maxHeight: 360, overflowY: 'auto' }}>
          {filtered.map(u => (
            <label key={u.id} style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: 8,
              border: '1px solid var(--border)', borderRadius: 6, cursor: 'pointer',
              background: selected.has(u.id) ? 'var(--accent-soft, var(--c-blue-100))' : 'transparent'
            }}>
              <input
                type="checkbox"
                checked={selected.has(u.id)}
                onChange={() => toggleOne(u.id)}
                disabled={u.id === user.id}
              />
              <span style={{ flex: 1 }}>{u.name}</span>
              <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{ROLE_AR[u.role] || u.role}</span>
            </label>
          ))}
          {filtered.length === 0 && (
            <div style={{ color: 'var(--text-muted)', padding: 12 }}>لا يوجد مستخدمون</div>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button className="btn btn-primary" onClick={send} disabled={sending}>
          {sending ? 'جاري الإرسال...' : `إرسال (${selected.size})`}
        </button>
      </div>
    </div>
  );
}
